/**
 * @fileoverview Shared module-option normalizer — the single process that turns
 *               raw module records into the `{ key, label, enabled }` options
 *               rendered anywhere modules are selectable.
 * @module core-domain/modules/moduleOptions
 *
 * Responsibilities:
 * - Normalize raw module records (system `modules` catalog docs or embedded
 *   `tenants.modules` entries) into selector options keyed by module `code`.
 * - Drop deleted/deprecated/empty-code records and dedupe by code.
 * - Sort options by display label for stable UI ordering.
 *
 * Dependencies:
 * - None — pure function.
 *
 * Architecture:
 * - The modules collection in the system DB is the source of truth for module
 *   codes and names. Hosts inject their own retrieval seam: system-web reads
 *   the synced `modules` Dexie table, tenant-web reads the embedded
 *   `tenants.modules` projection synced into `system_tenant_ref`. Both feed
 *   this normalizer so selector lists cannot drift from the catalog.
 * - Module CRUD surfaces (Modules page, onboarding wizard) own catalog reads
 *   directly and do not need this helper.
 *
 * Notes:
 * - `enabled` is only meaningful for tenant-scoped sources (embedded
 *   `tenants.modules[].isEnabled`); catalog-level docs leave it false.
 */

/**
 * @contract ModuleOptionSource
 * Purpose: Minimal shape of a raw module record accepted by `toModuleOptions`.
 * Fields:  code, name, status, isEnabled, _deleted
 * Rules:   `code` is the stable identifier stored on lookup categories/values;
 *          `name` provides the display label; `status === 'deprecated'` and
 *          `_deleted === true` records are excluded from options.
 * Sync:    N/A — input records arrive already synced into Dexie.
 */
export interface ModuleOptionSource {
  code?: string | null;
  name?: string | null;
  status?: string | null;
  isEnabled?: boolean | null;
  _deleted?: boolean | null;
}

/**
 * @contract ModuleOption
 * Purpose: One selectable module option for UI dropdowns/multi-selects.
 * Fields:  key (module code stored on records), label (display name),
 *          enabled (tenant assignment flag — only meaningful when the source
 *          carried `isEnabled`, e.g. embedded `tenants.modules` entries).
 * Rules:   `key` is always a non-empty trimmed module code.
 * Sync:    Derived from synced data; never persisted itself.
 */
export interface ModuleOption {
  key: string;
  label: string;
  enabled: boolean;
}

/**
 * @process toModuleOptions
 * Steps:
 *   1. Skip records that are deleted, deprecated, or missing a code.
 *   2. Dedupe by trimmed code (first record wins).
 *   3. Map to `{ key: code, label: name || code, enabled }`.
 *   4. Sort by label (locale-aware).
 * Inputs:  raw module records from a synced source.
 * Outputs: `ModuleOption[]` ready for selector UI.
 * Failure Handling: non-object/null entries are skipped, never thrown.
 * Idempotency: REQUIRED — pure function, safe to re-run on any snapshot.
 */
export function toModuleOptions(
  modules: readonly (ModuleOptionSource | null | undefined)[] | null | undefined,
): ModuleOption[] {
  const byCode = new Map<string, ModuleOption>();
  for (const raw of modules ?? []) {
    if (!raw || raw._deleted === true) continue;
    if (String(raw.status ?? '').trim().toLowerCase() === 'deprecated') continue;
    const code = String(raw.code ?? '').trim();
    if (!code || byCode.has(code)) continue;
    byCode.set(code, {
      key: code,
      label: String(raw.name ?? '').trim() || code,
      enabled: raw.isEnabled === true,
    });
  }
  return Array.from(byCode.values()).sort((a, b) => a.label.localeCompare(b.label));
}
